import { useMutation, useQuery } from '@apollo/client';
import { Col, Row, Tooltip } from 'antd';
import * as clipboard from 'clipboard-polyfill/text';
import classNames from 'classnames';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Route, generatePath, useHistory } from 'react-router';

import { shipmentConversions } from '../app/data/shipmentConversions';
import {
  JOB_QUERY,
  SHARED_SHIPMENT_QUERY,
  SHARE_SHIPMENT_MUTATION,
} from '../app/graphql/jobQueries';
import { setShipmentDetailSummaryView } from '../app/redux/shipmentDetail';
import routes from '../app/routes';
import { extractGraphqlEntity } from '../common/utils/graphqlUtils';
import IconButton from '../components/buttons/IconButton';
import { renderDataStateIndicator } from '../components/data/dataStateHandlers';
import { useFlashMessageContext } from '../components/dialogs/FlashMessageProvider';
import { OverrideSettingsContextProvider } from '../components/domainSpecific/settingsElements';
import { SimpleCard, SimpleCardTheme } from '../components/layout/cardElements';
import { Flex1 } from '../components/layout/layoutElements';
import { useLayoutElementsStyles } from '../components/layout/layoutElements.styles';
import { useResponsiveQueries } from '../components/responsive/responsiveQueries';
import { DEFAULT_POLL_REFRESH_INTERVAL } from '../config/constants';
import { OrderSuccessDialog } from '../dialogs/shipments/newShipmentDialogs';
import { useAccounts } from '../hooks/data/auth';
import { cssVariables } from '../styles/cssVariables';
import DetailPage from '../templates/DetailPage';
import { pxToNumber } from '../utils/cssUtils';
import { BackLinkDetailPageHeader } from '../widgets/detailPageHeaders';
import ShipmentDetailMap from './shipmentDetail/ShipmentDetailMap';
import ShipmentMilestonesSteps from './shipmentDetail/ShipmentMilestonesSteps';
import { SHIPMENT_SUMMARY_VIEWS } from './shipmentDetail/shipmentSummaries';
import { ShipmentDetailResponsiveContent } from './shipmentDetail/shipmentSummaries/ShipmentDetailResponsiveContent';
import { useTooltipStyles } from '../styles/tooltip.styles';
import { useShipmentDetailPageStyles } from './ShipmentDetailPage.styles';

const Card = props => (
  <SimpleCard {...props} theme={SimpleCardTheme.ROUND_BORDERED} />
);

function LargeView({ shipment, children }) {
  const { styles } = useShipmentDetailPageStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  return (
    <Row
      className={classNames(layoutStyles.flex1, 'flex-nowrap')}
      gutter={pxToNumber(cssVariables.spaceNorm2)}
    >
      {children}
      <Col
        className={classNames(
          layoutStyles.flexCol,
          layoutStyles.flex1,
          'spaces-vert-norm2'
        )}
      >
        <Card
          className={styles.shipmentMilestonesCard}
          titleId="shipmentDetail.shipmentMilestones"
        >
          <ShipmentMilestonesSteps shipment={shipment} />
        </Card>
        <Flex1>
          <ShipmentDetailMap shipment={shipment} />
        </Flex1>
      </Col>
    </Row>
  );
}

export function RepeatOrderButton({ shipment }) {
  const { styles } = useTooltipStyles();
  const { styles: pageStyles } = useShipmentDetailPageStyles();
  const { push } = useHistory();
  return (
    <Tooltip
      placement="bottom"
      classNames={{ root: `${styles.sm} nl-as-newline ${styles.textCenter}` }}
      title={<FormattedMessage id="shipmentDetail.repeatOrder.tooltip" />}
    >
      <IconButton
        className={pageStyles.headerButton}
        labelId="shipmentDetail.repeatOrder"
        icon="copy"
        data-subject="shipment-detail"
        data-action="repeat-order"
        disabled={!shipment}
        onClick={() => {
          push(
            generatePath(routes.repeatShipment, {
              jobNumber: shipment.jobNumber,
            })
          );
        }}
      />
    </Tooltip>
  );
}

export function ReverseOrderButton({ shipment }) {
  const { styles } = useTooltipStyles();
  const { styles: pageStyles } = useShipmentDetailPageStyles();
  const { push } = useHistory();
  return (
    <Tooltip
      placement="bottom"
      classNames={{ root: `${styles.sm} nl-as-newline ${styles.textCenter}` }}
      title={<FormattedMessage id="shipmentDetail.reverseOrder.tooltip" />}
    >
      <IconButton
        className={pageStyles.headerButton}
        labelId="shipmentDetail.reverseOrder"
        icon="exchange-alt"
        data-subject="shipment-detail"
        data-action="reverse-order"
        disabled={!shipment}
        onClick={() => {
          push(
            generatePath(routes.reverseShipment, {
              jobNumber: shipment.jobNumber,
            })
          );
        }}
      />
    </Tooltip>
  );
}

function ShareButton({ shipment }) {
  const [share, { loading: sharing }] = useMutation(SHARE_SHIPMENT_MUTATION);
  const { errorMessage, successMessage } = useFlashMessageContext();
  const { styles } = useShipmentDetailPageStyles();

  return (
    <IconButton
      className={styles.headerButton}
      labelId="shipmentDetail.share"
      icon="link"
      disabled={sharing}
      onClick={async () => {
        const { data } = await share({
          variables: { jobNumber: shipment.jobNumber },
        });
        const { token, url } = extractGraphqlEntity(data);
        const finalUrl =
          url ||
          `${window.location.origin}${generatePath(
            routes.sharingShipmentDetail,
            { token }
          )}`;
        try {
          clipboard.writeText(finalUrl);
          successMessage({ contentId: 'copyToClipboard.success' });
        } catch (err) {
          errorMessage({ contentId: 'copyToClipboard.failure' });
        }
      }}
    />
  );
}

function PrintBolButton({ bolUrl }) {
  const { styles } = useShipmentDetailPageStyles();
  return bolUrl ? (
    <IconButton
      className={styles.headerButton}
      labelId="shipmentDetail.printBolLabel"
      icon="print"
      onClick={() => window.open(bolUrl, '__blank')}
    />
  ) : null;
}

function HeaderButtons({ shipment }) {
  const bolUrl = shipment?.documents?.bolUrl;
  const shareable = shipment?.metadata?.shareable;

  return (
    <Row align="middle" gutter={pxToNumber(cssVariables.spaceSm)}>
      <Col>
        <RepeatOrderButton shipment={shipment} />
      </Col>
      <Col>
        <ReverseOrderButton shipment={shipment} />
      </Col>
      {shareable && (
        <Col>
          <ShareButton shipment={shipment} />
        </Col>
      )}
      {bolUrl && (
        <Col>
          <PrintBolButton bolUrl={bolUrl} />
        </Col>
      )}
    </Row>
  );
}

function ShipmentDetailPageInner({
  shipment,
  indicator,
  showBackButton,
  showHeaderButtons,
}) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setShipmentDetailSummaryView(SHIPMENT_SUMMARY_VIEWS[0].name));
  }, [dispatch]);

  const media = useResponsiveQueries();
  const isSm = media.sm || media.xs;

  return (
    <DetailPage
      id="ShipmentDetailPage"
      header={
        <BackLinkDetailPageHeader
          backTargetId={showBackButton && 'monitor.short'}
          backLink={showBackButton && routes.monitor}
          titleId="shipmentDetail.title"
          buttons={showHeaderButtons && <HeaderButtons shipment={shipment} />}
        />
      }
      wrapInScrollbar={isSm}
    >
      <OverrideSettingsContextProvider unitSystem={shipment.unitSystem}>
        {indicator || (
          <ShipmentDetailResponsiveContent
            shipment={shipment}
            summariesSharedProps={{ showAirlineFlightStatus: true }}
            largeViewComponent={LargeView}
          />
        )}
      </OverrideSettingsContextProvider>
    </DetailPage>
  );
}

export default function ShipmentDetailPage({ match: { url, params } }) {
  const { replace } = useHistory();
  const { data, loading, error } = useQuery(JOB_QUERY, {
    variables: { jobNumber: params.jobNumber },
    pollInterval: DEFAULT_POLL_REFRESH_INTERVAL,
    fetchPolicy: 'cache-and-network',
  });
  const silentReload = loading && data;
  const indicator =
    !silentReload && renderDataStateIndicator({ loading, error, data });

  const { customerAccounts } = useAccounts();
  const rawShipment = !indicator && extractGraphqlEntity(data);
  const shipment =
    rawShipment &&
    shipmentConversions.graphqlToDetail(rawShipment, { customerAccounts });

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setShipmentDetailSummaryView(SHIPMENT_SUMMARY_VIEWS[0].name));
  }, [dispatch]);

  return (
    <>
      <ShipmentDetailPageInner
        shipment={shipment}
        indicator={indicator}
        showHeaderButtons
        showBackButton
      />
      <Route
        path={routes.shipmentDetailSuccess}
        render={routeProps => (
          <OrderSuccessDialog {...routeProps} onOk={() => replace(url)} />
        )}
      />
    </>
  );
}

export function SharedShipmentDetailPage({ match: { params } }) {
  const { data, loading, error } = useQuery(SHARED_SHIPMENT_QUERY, {
    variables: { token: params.token },
    pollInterval: DEFAULT_POLL_REFRESH_INTERVAL,
    fetchPolicy: 'cache-and-network',
  });
  const silentReload = loading && data;
  const indicator =
    !silentReload && renderDataStateIndicator({ loading, error, data });

  const rawShipment = !indicator && extractGraphqlEntity(data);
  const shipment =
    rawShipment && shipmentConversions.graphqlToDetail(rawShipment, {});

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setShipmentDetailSummaryView(SHIPMENT_SUMMARY_VIEWS[0].name));
  }, [dispatch]);

  return (
    <ShipmentDetailPageInner
      shipment={shipment}
      indicator={indicator}
      showHeaderButtons={false}
      showBackButton={false}
    />
  );
}
